
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AdUnit } from "@/components/ads/AdUnit";
import { CategorySection } from "@/components/history/CategorySection";
import { measurementTypes, findCategoryForUnit } from "@/components/history/utils/measurement-types";

export default function History() {
  const [searchParams] = useSearchParams();
  const previousUnit = searchParams.get('fromUnit');

  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [expandedSubcategories, setExpandedSubcategories] = useState<{ [category: string]: string[] }>({});

  useEffect(() => {
    if (previousUnit) {
      const unitLocation = findCategoryForUnit(previousUnit);
      if (unitLocation) {
        setExpandedCategories(prev => 
          prev.includes(unitLocation.category) ? prev : [...prev, unitLocation.category]
        );
        setExpandedSubcategories(prev => ({
          ...prev,
          [unitLocation.category]: [
            ...(prev[unitLocation.category] || []),
            unitLocation.subcategory
          ]
        }));
      }
    }
    
    // Scroll to top when History page loads
    window.scrollTo(0, 0);
  }, [previousUnit]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleSubcategory = (category: string, subcategory: string) => {
    setExpandedSubcategories(prev => {
      const categorySubcategories = prev[category] || [];
      const updatedSubcategories = categorySubcategories.includes(subcategory)
        ? categorySubcategories.filter(sc => sc !== subcategory)
        : [...categorySubcategories, subcategory];
      
      return {
        ...prev,
        [category]: updatedSubcategories
      };
    });
  };

  if (!measurementTypes || typeof measurementTypes !== "object") {
    return <div className="p-8 text-center text-red-600">Error: Data is missing or corrupted.</div>;
  }

  return (
    <div className="relative flex flex-col min-h-screen">
      <div className="flex-grow p-4">
        <h1 className="text-2xl md:text-3xl font-cinzel text-manuscript-primary mb-6 relative z-10 text-center">
          Historical Units of Measurement: Complete Guide & Conversion Encyclopedia
        </h1>

        <div className="max-w-full w-full mx-auto relative z-10 space-y-3">
          {Object.entries(measurementTypes).map(([measurementType, categories]) => (
            <CategorySection
              key={measurementType}
              measurementType={measurementType}
              categories={categories}
              isExpanded={expandedCategories.includes(measurementType)}
              expandedSubcategories={expandedSubcategories[measurementType] || []}
              onToggleCategory={() => toggleCategory(measurementType)}
              onToggleSubcategory={(subcategory) => toggleSubcategory(measurementType, subcategory)}
            />
          ))}
        </div>
      </div>
      
      {/* Ad Space at Bottom */}
      <div className="w-full p-4 mt-4">
        <AdUnit 
          adSlot="3043090605"
          className="min-h-[90px] w-full bg-gray-100/50 rounded-lg"
          adFormat="horizontal"
          desktopOnly
        />
      </div>
    </div>
  );
};
